import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE, AD_TYPE } from '../../constants/gaEnums';
import { trackContentSave } from '../../lib/gaEvents';
import { Alert, Box, Snackbar, Stack, useMediaQuery, useTheme } from '@mui/material';
import ProgressBar from '../../components/common/ProgressBar';
import AlbumCover from '../../components/common/AlbumCover';
import { useUserStore } from '../../stores/userStore';
import {
  useAlbumDetail,
  useAlbumDetailBySlug,
  useManageLatestAlbum,
  useToggleBookmark,
  useToggleHomeAlbumFeature,
  useToggleStarred,
  useUpdateRecommendedAlbumComment,
} from './hooks/useAlbumDetail';
import { useQuery } from '@tanstack/react-query';
import { useAlbumReviews } from './hooks/useAlbumReviews';
import api from '../../api/api';
import PageLayout from '../../components/layout/PageLayout';
import Tracklist from './components/album/Tracklist';
import AlbumInfoSection from './components/album/AlbumInfoSection';
import MyReviewSection from './components/my-review/MyReviewSection';
import UserReviewSection from './components/user-review/UserReviewSection';
import CriticReviewSection from './components/critic-review/CriticReviewSection';
import RelatedAlbumsSection from './components/album/RelatedAlbumsSection';
import OtherVersionsSection from './components/album/OtherVersionsSection';
import GoogleAdSense, { AD_SLOT } from '../../components/ad/GoogleAdSense';
import InFeedAd from '../../components/ad/InFeedAd';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import PostpopRecommendationNote from '../../components/common/PostpopRecommendationNote';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const useSpotifyUrl = (albumName, artistName, albumId, enabled = true) =>
  useQuery({
    queryKey: ['spotifyUrl', albumName, artistName, albumId],
    queryFn: async () => {
      const res = await api.get('/albums/spotify-url', {
        params: { album_name: albumName, artist_name: artistName, album_id: albumId },
        suppressGlobalError: true,
      });
      return res.data?.data?.spotify_url || null;
    },
    enabled: enabled && !!albumName && !!artistName,
    staleTime: 1000 * 60 * 60 * 24, // 24 hour
    retry: false,
  });

const ReviewDetail = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [registrationNoticeOpen, setRegistrationNoticeOpen] = useState(
    Boolean(location.state?.directCatalogCreated),
  );
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { user, isAdmin } = useUserStore();
  // 슬러그 기반 또는 기존 ID 기반 조회 (두 훅 모두 항상 호출, 각각 enabled 조건으로 제어)
  const slugResult = useAlbumDetailBySlug(albumSlug);
  const { data: directAlbumData, isLoading: directLoading } = useAlbumDetail(paramSource, paramId);

  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;
  const albumData = albumSlug ? slugResult.data : directAlbumData;
  const albumLoading = albumSlug ? slugResult.isLoading : directLoading;

  useEffect(() => {
    if (!location.state?.directCatalogCreated) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state?.directCatalogCreated, navigate]);

  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_POST,
      contentType: CONTENT_TYPE.ALBUM,
      contentId: sourceId,
      contentName: albumData?.title,
    },
    { ready: !!sourceId },
  );

  const toggleBookmark = useToggleBookmark(albumSource, sourceId);
  const toggleStarred = useToggleStarred(albumSource, sourceId);
  const toggleHomeAlbumFeature = useToggleHomeAlbumFeature(albumSource, sourceId);
  const updateRecommendedAlbumComment = useUpdateRecommendedAlbumComment(albumSource, sourceId);
  const manageLatestAlbum = useManageLatestAlbum(albumSource, sourceId);
  const { data: allReviews = [] } = useAlbumReviews(albumSource, sourceId);

  // Spotify URL을 albumInfo와 병렬로 가져오기 (캐시된 URL이 없을 때만)
  const needsSpotifyFetch = albumData && !albumData.urls?.spotify;
  const { data: spotifyUrl } = useSpotifyUrl(
    albumData?.title,
    albumData?.artist?.name,
    albumData?.id,
    needsSpotifyFetch,
  );

  // albumInfo에 spotifyUrl 병합
  const albumInfo = useMemo(() => {
    if (!albumData) return null;
    if (!needsSpotifyFetch || !spotifyUrl) return albumData;
    return {
      ...albumData,
      urls: {
        ...albumData.urls,
        spotify: spotifyUrl,
      },
    };
  }, [albumData, needsSpotifyFetch, spotifyUrl]);

  const isLoading = albumLoading;

  const currentAlbumId = useMemo(() => {
    if (!albumInfo) return null;
    return albumInfo.id ?? (albumSource === 'local' ? sourceId : null);
  }, [albumInfo, albumSource, sourceId]);

  const pagination = useMemo(() => {
    const totalItems = allReviews.length;
    return { totalItems };
  }, [allReviews.length]);

  const recommendationComment = albumInfo?.recommendationComment?.trim();

  const handleLatestAlbumRegionMove = (displayRegion) => {
    manageLatestAlbum.mutate({ albumInfo, action: 'region', displayRegion });
  };

  const handleLatestAlbumHide = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'hide' });
  };

  const handleLatestAlbumRegister = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'register' });
  };

  const handleRecommendedAlbumRemove = () => {
    manageLatestAlbum.mutate({ albumInfo, action: 'removeRecommended' });
  };

  const renderRecommendationNote = (sx = {}) => {
    if (!recommendationComment) return null;

    return (
      <PostpopRecommendationNote
        comment={recommendationComment}
        label="앨범 노트"
        lineClamp="unset"
        commentSx={{ fontSize: { xs: 14, lg: 16 } }}
        sx={sx}
      />
    );
  };

  return (
    <PageLayout>
      <Snackbar
        open={registrationNoticeOpen}
        autoHideDuration={3500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setRegistrationNoticeOpen(false)}
      >
        <Alert severity="success" variant="filled" onClose={() => setRegistrationNoticeOpen(false)}>
          앨범 등록이 완료되었습니다.
        </Alert>
      </Snackbar>
      {albumInfo && albumInfo.artist && (
        <>
          <OpenInAppBanner
            type="album"
            title={albumInfo.title}
            subtitle={`${albumInfo.artist.name} · 앨범`}
            image={albumInfo.imageUrl}
          />
          <SEO
            title={`${albumInfo.artist.name} - ${albumInfo.title} 앨범 리뷰`}
            description={`${albumInfo.title} by ${albumInfo.artist.name} 발매일: ${albumInfo.releaseDate?.split('.')[0] || ''}. 장르: ${albumInfo.genres?.[0] || ''}`}
            image={albumInfo.imageUrl}
            url={
              albumSlug
                ? `${SITE_URL}/reviews/${albumSlug}`
                : `${SITE_URL}/reviews/${albumSource}/${sourceId}`
            }
            rawTitle
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Reviews',
                    item: `${SITE_URL}/reviews`,
                  },
                  { '@type': 'ListItem', position: 3, name: albumInfo.title },
                ],
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'MusicAlbum',
                name: albumInfo.title,
                image: albumInfo.imageUrl,
                url: albumSlug
                  ? `${SITE_URL}/reviews/${albumSlug}`
                  : `${SITE_URL}/reviews/${albumSource}/${sourceId}`,
                datePublished: albumInfo.releaseDate
                  ? albumInfo.releaseDate.replace(
                      /(\d+)\.(\d+)\.(\d+)/,
                      (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                    )
                  : undefined,
                genre: albumInfo.genres?.length ? albumInfo.genres : undefined,
                numTracks: albumInfo.totalTracks || undefined,
                byArtist: {
                  '@type': 'MusicGroup',
                  name: albumInfo.artist.name,
                },
                ...(albumInfo.reviewCount > 0 && {
                  aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: albumInfo.averageRating.toFixed(1),
                    bestRating: '5',
                    worstRating: '0.5',
                    ratingCount: String(albumInfo.reviewCount),
                  },
                }),
                ...(allReviews.length > 0 && {
                  review: allReviews.slice(0, 3).map((r) => ({
                    '@type': 'Review',
                    author: { '@type': 'Person', name: r.user.nickname },
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: r.rating,
                      bestRating: 5,
                      worstRating: 0.5,
                    },
                    datePublished: r.createdAt
                      ? r.createdAt.replace(
                          /(\d+)\.(\d+)\.(\d+)/,
                          (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                        )
                      : undefined,
                  })),
                }),
              })}
            </script>
          </Helmet>
        </>
      )}
      <ProgressBar isLoading={isLoading}>
        <Box sx={{ maxWidth: '980px', mx: 'auto' }}>
          {albumInfo && albumInfo.artist && (
            <>
              {/* LG Layout */}
              {isLgUp && (
                <Stack spacing={5}>
                  {/* Top Row */}
                  <Box sx={{ display: 'flex', gap: 5, alignItems: 'stretch' }}>
                    {/* Top Left: AlbumCover */}
                    <Box sx={{ width: '50%', maxWidth: 380, flexShrink: 0 }}>
                      <AlbumCover
                        imageUrl={albumInfo.imageUrl}
                        altText={albumInfo.title}
                        isStarred={albumInfo.isStarred}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                    {/* Top Right: AlbumInfoSection */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AlbumInfoSection
                        albumInfo={albumInfo}
                        isAdmin={isAdmin}
                        pageType={PAGE_TYPE.REVIEWS_POST}
                        onBookmarkToggle={() => {
                          const wasBookmarked = albumInfo.isBookmarked;
                          toggleBookmark.mutate(
                            { albumInfo, currentAlbumId },
                            {
                              onSuccess: (data) => {
                                if (!wasBookmarked) {
                                  trackContentSave(
                                    {
                                      contentType: CONTENT_TYPE.ALBUM,
                                      contentId: sourceId,
                                      contentName: albumInfo?.title,
                                      pageType: PAGE_TYPE.REVIEWS_POST,
                                    },
                                    user,
                                  );
                                }
                              },
                            },
                          );
                        }}
                        onStarredToggle={() => toggleStarred.mutate({ albumInfo })}
                        onHomeLatestToggle={() =>
                          toggleHomeAlbumFeature.mutate({ albumInfo, target: 'latest' })
                        }
                        onHomeRecommendedToggle={(recommendationComment, recommendationCommentEn) =>
                          toggleHomeAlbumFeature.mutate({
                            albumInfo,
                            target: 'recommended',
                            recommendationComment,
                            recommendationCommentEn,
                          })
                        }
                        onRecommendedCommentUpdate={(
                          recommendationComment,
                          recommendationCommentEn,
                        ) =>
                          updateRecommendedAlbumComment.mutate({
                            albumInfo,
                            recommendationComment,
                            recommendationCommentEn,
                          })
                        }
                        onLatestAlbumRegionMove={handleLatestAlbumRegionMove}
                        onLatestAlbumHide={handleLatestAlbumHide}
                        onLatestAlbumRegister={handleLatestAlbumRegister}
                        onRecommendedAlbumRemove={handleRecommendedAlbumRemove}
                      />
                    </Box>
                  </Box>
                  {/* Bottom Row */}
                  <Box sx={{ display: 'flex', gap: 5 }}>
                    {/* Bottom Left: Tracklist + Other Versions + Ad */}
                    <Box sx={{ width: '50%', maxWidth: 380, flexShrink: 0 }}>
                      {renderRecommendationNote({ mb: '40px' })}
                      <Tracklist tracks={albumInfo.tracks} />
                      <Box sx={{ mt: '40px' }}>
                        <OtherVersionsSection albumInfo={albumInfo} />
                      </Box>
                      <Box sx={{ position: 'sticky', top: 80, p: '40px' }}>
                        <GoogleAdSense
                          adSlot={AD_SLOT.REVIEWS_POST_LEFT_STICKY}
                          adType={AD_TYPE.SIDEBAR_300_600}
                          pageType={PAGE_TYPE.REVIEWS_POST}
                          sx={{ width: 300, height: 600 }}
                        />
                      </Box>
                    </Box>
                    {/* Bottom Right: MyReview + CriticReviewSection + UserReviewSection + RelatedAlbumsSection */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <MyReviewSection
                        historicalAlbumId={albumData?.hasMyHistoricalReviews ? albumData.id : null}
                        albumSource={albumSource}
                        sourceId={sourceId}
                        albumTitle={albumInfo?.title}
                      />
                      <Box mt={5}>
                        <CriticReviewSection albumId={currentAlbumId} />
                      </Box>
                      <Box mt={5}>
                        <UserReviewSection
                          reviews={allReviews}
                          totalCount={pagination.totalItems}
                          albumSource={albumSource}
                          sourceId={sourceId}
                        />
                      </Box>
                      <Box mt={5}>
                        <RelatedAlbumsSection albumInfo={albumInfo} />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              )}

              {/* MD Layout */}
              {!isLgUp && isMdUp && (
                <>
                  <Box sx={{ display: 'flex', gap: 2 }} mb={5}>
                    <Box sx={{ width: '40.55%', flexShrink: 0 }}>
                      <AlbumCover
                        imageUrl={albumInfo.imageUrl}
                        altText={albumInfo.title}
                        isStarred={albumInfo.isStarred}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AlbumInfoSection
                        albumInfo={albumInfo}
                        isAdmin={isAdmin}
                        pageType={PAGE_TYPE.REVIEWS_POST}
                        onBookmarkToggle={() => {
                          const wasBookmarked = albumInfo.isBookmarked;
                          toggleBookmark.mutate(
                            { albumInfo, currentAlbumId },
                            {
                              onSuccess: (data) => {
                                if (!wasBookmarked) {
                                  trackContentSave(
                                    {
                                      contentType: CONTENT_TYPE.ALBUM,
                                      contentId: sourceId,
                                      contentName: albumInfo?.title,
                                      pageType: PAGE_TYPE.REVIEWS_POST,
                                    },
                                    user,
                                  );
                                }
                              },
                            },
                          );
                        }}
                        onStarredToggle={() => toggleStarred.mutate({ albumInfo })}
                        onHomeLatestToggle={() =>
                          toggleHomeAlbumFeature.mutate({ albumInfo, target: 'latest' })
                        }
                        onHomeRecommendedToggle={(recommendationComment, recommendationCommentEn) =>
                          toggleHomeAlbumFeature.mutate({
                            albumInfo,
                            target: 'recommended',
                            recommendationComment,
                            recommendationCommentEn,
                          })
                        }
                        onRecommendedCommentUpdate={(
                          recommendationComment,
                          recommendationCommentEn,
                        ) =>
                          updateRecommendedAlbumComment.mutate({
                            albumInfo,
                            recommendationComment,
                            recommendationCommentEn,
                          })
                        }
                        onLatestAlbumRegionMove={handleLatestAlbumRegionMove}
                        onLatestAlbumHide={handleLatestAlbumHide}
                        onLatestAlbumRegister={handleLatestAlbumRegister}
                        onRecommendedAlbumRemove={handleRecommendedAlbumRemove}
                      />
                    </Box>
                  </Box>
                  <Stack spacing={5}>
                    {renderRecommendationNote()}
                    <Tracklist tracks={albumInfo.tracks} />
                    <OtherVersionsSection albumInfo={albumInfo} />
                    <MyReviewSection
                      historicalAlbumId={albumData?.hasMyHistoricalReviews ? albumData.id : null}
                      albumSource={albumSource}
                      sourceId={sourceId}
                      albumTitle={albumInfo?.title}
                    />
                    <InFeedAd pageType={PAGE_TYPE.REVIEWS_POST} />
                    <CriticReviewSection albumId={currentAlbumId} />
                    <UserReviewSection
                      reviews={allReviews}
                      totalCount={pagination.totalItems}
                      albumSource={albumSource}
                      sourceId={sourceId}
                    />
                    <RelatedAlbumsSection albumInfo={albumInfo} />
                  </Stack>
                </>
              )}

              {/* XS Layout */}
              {!isMdUp && (
                <>
                  <Stack spacing={2} mb={'30px'}>
                    <AlbumCover
                      imageUrl={albumInfo.imageUrl}
                      altText={albumInfo.title}
                      isStarred={albumInfo.isStarred}
                      starIconSize="15%" // xs는 기본 20% 이지만, 여기는 예외적으로 15%
                      sx={{ width: '100%' }}
                    />
                    <AlbumInfoSection
                      albumInfo={albumInfo}
                      albumId={currentAlbumId}
                      isAdmin={isAdmin}
                      pageType={PAGE_TYPE.REVIEWS_POST}
                      onBookmarkToggle={() => {
                        const wasBookmarked = albumInfo.isBookmarked;
                        toggleBookmark.mutate(
                          { albumInfo, currentAlbumId },
                          {
                            onSuccess: (data) => {
                              if (!wasBookmarked) {
                                trackContentSave(
                                  {
                                    contentType: CONTENT_TYPE.ALBUM,
                                    contentId: sourceId,
                                    contentName: albumInfo?.title,
                                    pageType: PAGE_TYPE.REVIEWS_POST,
                                  },
                                  user,
                                );
                              }
                            },
                          },
                        );
                      }}
                      onStarredToggle={() => toggleStarred.mutate({ albumInfo })}
                      onHomeLatestToggle={() =>
                        toggleHomeAlbumFeature.mutate({ albumInfo, target: 'latest' })
                      }
                      onHomeRecommendedToggle={(recommendationComment, recommendationCommentEn) =>
                        toggleHomeAlbumFeature.mutate({
                          albumInfo,
                          target: 'recommended',
                          recommendationComment,
                          recommendationCommentEn,
                        })
                      }
                      onRecommendedCommentUpdate={(
                        recommendationComment,
                        recommendationCommentEn,
                      ) =>
                        updateRecommendedAlbumComment.mutate({
                          albumInfo,
                          recommendationComment,
                          recommendationCommentEn,
                        })
                      }
                      onLatestAlbumRegionMove={handleLatestAlbumRegionMove}
                      onLatestAlbumHide={handleLatestAlbumHide}
                      onLatestAlbumRegister={handleLatestAlbumRegister}
                      onRecommendedAlbumRemove={handleRecommendedAlbumRemove}
                    />
                  </Stack>
                  {renderRecommendationNote({ mb: '30px' })}
                  <Stack spacing={'30px'}>
                    <Tracklist tracks={albumInfo.tracks} />
                    <OtherVersionsSection albumInfo={albumInfo} />
                    <MyReviewSection
                      historicalAlbumId={albumData?.hasMyHistoricalReviews ? albumData.id : null}
                      albumSource={albumSource}
                      sourceId={sourceId}
                      albumTitle={albumInfo?.title}
                    />
                    <InFeedBoxAd pageType={PAGE_TYPE.REVIEWS_POST} />
                    <CriticReviewSection albumId={currentAlbumId} />
                    <UserReviewSection
                      reviews={allReviews}
                      totalCount={pagination.totalItems}
                      albumSource={albumSource}
                      sourceId={sourceId}
                    />
                    <RelatedAlbumsSection albumInfo={albumInfo} />
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </ProgressBar>
    </PageLayout>
  );
};

export default ReviewDetail;
